/**
 * Reguły formularza lokalu w adminie — LUSTRO `mobile/src/utils/apartmentFormRules.ts` (PR #341,
 * Bronek 2026-10-02: „kliknąłem BRAK UWAG – jest na zielono, ale zalecenia pozostały … To samo przy
 * NIEOBECNOŚCI"). Wybór statusu, którego zalecenia nie obejmują, przy niepustych zaleceniach pyta
 * „Usunąć wpisane zalecenia?"; potwierdzenie czyści zalecenia, anulowanie zostawia poprzedni status.
 *
 * `odmowa_wpisu` CELOWO poza listą (jak na tablecie): kontrola się odbyła, zalecenia idą do CEEB
 * i na PDF. Zmieniasz listę tutaj → zmień też w mobilce (i odwrotnie).
 */
// Statusy jako `string`: typ `InspectionStatus` admina zna tylko 3 statusy z formularza (odmów w
// adminie się nie wybiera), a lista ma być 1:1 z tabletem — także dla rekordu z odmową z terenu.
export const STATUSES_CLEARING_RECOMMENDATIONS: readonly string[] = [
  'brak_uwag',
  'brak_dostepu',
  'odmowa_lokatora',
];

/** Czy wybór statusu `next` wymaga pytania o usunięcie zaleceń lokalu. */
export function shouldAskToClearRecommendations(
  next: string,
  recommendations: string | null | undefined,
): boolean {
  return STATUSES_CLEARING_RECOMMENDATIONS.includes(next) && !!(recommendations && recommendations.trim());
}

/**
 * Wynik wyboru statusu. `confirmed` = potwierdzono usunięcie zaleceń (ignorowane, gdy pytanie nie
 * było potrzebne). Odmowa zostawia POPRZEDNI status i zalecenia bez zmian.
 */
export function applyStatusChoice<S extends string>(
  state: { status: S; recommendations: string },
  next: S,
  confirmed: boolean,
): { status: S; recommendations: string } {
  if (!shouldAskToClearRecommendations(next, state.recommendations)) {
    return { status: next, recommendations: state.recommendations };
  }
  return confirmed ? { status: next, recommendations: '' } : state;
}

/**
 * Etykiety KAFLI wyboru statusu w formularzu lokalu — LUSTRO `mobile/src/constants/statusTileLabels.ts`
 * (parytet: backend/test/statusTileLabelsParity.test.js). Osobno od `STATUS_LABELS` (ds/StatusMap), bo kanon
 * celowo mapuje obie odmowy na jedno „Odmowa" — w pickerze dwa kafle „Odmowa" to gwarantowana pomyłka.
 * Kolejność = kolejność kafli na tablecie (zadanie 4, 2026-10-21: przegląd z papieru wprowadzany w panelu).
 */
export const STATUS_TILE_ORDER = [
  'brak_dostepu',
  'odmowa_lokatora',
  'zalecenie',
  'brak_uwag',
  'odmowa_wpisu',
] as const;

export const STATUS_TILE_LABELS: Record<(typeof STATUS_TILE_ORDER)[number], string> = {
  brak_dostepu: 'Nieobecność',
  odmowa_lokatora: 'Lokator odmówił kontroli',
  zalecenie: 'Dodaj zalecenie',
  brak_uwag: 'Brak uwag',
  odmowa_wpisu: 'Odmowa wpisu do rejestru CEEB',
};

/**
 * Statusy, przy których formularz pokazuje zalecenia lokalu — kontrola się odbyła (lustro tabletu: zalecenia przy
 * „Odmowa wpisu" zostają i idą do CEEB/PDF; „Nieobecność" i „Lokator odmówił kontroli" je czyszczą).
 */
export const STATUSES_WITH_RECOMMENDATIONS: readonly string[] = ['zalecenie', 'brak_uwag', 'odmowa_wpisu'];

/** Funkcja lokalu — LUSTRO `APARTMENT_FUNCTION_OPTIONS` z mobile/src/screens/InspectionFormScreen.tsx (wartości AIR.function_type). */
export const APARTMENT_FUNCTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'lokal_mieszkalny', label: 'Lokal mieszkalny' },
  { value: 'lokal_niemieszkalny_uzytkowy', label: 'Lokal niemieszkalny/użytkowy' },
  { value: 'inny', label: 'Inny' },
];

/** Domyślna funkcja NOWEGO rekordu lokalu — jak tablet (`useState('lokal_mieszkalny')`). */
export const DEFAULT_APARTMENT_FUNCTION = 'lokal_mieszkalny';

/** Dzień kalendarza w strefie przeglądarki ('YYYY-MM-DD') — „dziś" dla walidacji daty kontroli. */
export function localIsoDay(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Data kontroli lokalu wpisywana w panelu (przegląd z papieru — data wizyty, nie dzień wprowadzenia): wymagana,
 * 'YYYY-MM-DD', nie z przyszłości (lustro tabletu: DateField max=today). Zwraca komunikat albo null.
 */
export function inspectionDateError(value: string, today: string = localIsoDay()): string | null {
  const v = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(new Date(`${v}T00:00:00`).getTime())) {
    return 'Podaj datę kontroli lokalu.';
  }
  if (v > today) return 'Data kontroli nie może być z przyszłości.';
  return null;
}
